import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Copy, Edit2, Trash2, ToggleLeft, ToggleRight, LineChart } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/AlertDialog";

const maskApiKey = (key, revealedKeys, forceMask = false) => {
  if (!key) return '';
  if (!forceMask && revealedKeys.has(key)) return key;
  return key.length > 8 ? `${key.slice(0, 4)}...${key.slice(-4)}` : key;
};

export function ApiKeyList({
  apiKeys = [],
  handleUpdateNickname,
  handleToggleKey,
  handleDeleteKeyConfirm,
  setSuccess,
  setError
}) {
  const navigate = useNavigate();
  const [editingKey, setEditingKey] = useState(null);
  const [editingNickname, setEditingNickname] = useState('');
  const [deleteKeyConfirm, setDeleteKeyConfirm] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState(new Set());

  // Group keys by product, then sort by nickname within each group
  const groupedKeys = useMemo(() => {
    const groups = {};

    apiKeys.forEach(key => {
      const productName = key.productName || 'Unknown Product';
      if (!groups[productName]) {
        groups[productName] = [];
      }
      groups[productName].push(key);
    });

    // Sort keys within each group by nickname (case-insensitive)
    Object.keys(groups).forEach(productName => {
      groups[productName].sort((a, b) => {
        const nicknameA = (a.nickname || '').toLowerCase();
        const nicknameB = (b.nickname || '').toLowerCase();
        return nicknameA.localeCompare(nicknameB);
      });
    });

    // Sort product names alphabetically
    const sortedProductNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));

    return sortedProductNames.map(productName => ({
      productName,
      keys: groups[productName]
    }));
  }, [apiKeys]);

  const startEditing = (key) => {
    setEditingKey(key.id);
    setEditingNickname(key.nickname || '');
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditingNickname('');
  };

  const saveNickname = (keyId) => {
    handleUpdateNickname(keyId, editingNickname);
    cancelEditing();
  };

  const confirmDelete = (key) => {
    setDeleteKeyConfirm(key);
  };

  const cancelDelete = () => {
    setDeleteKeyConfirm(null);
  };

  const executeDelete = () => {
    if (deleteKeyConfirm) {
      handleDeleteKeyConfirm(deleteKeyConfirm.id);
    }
    cancelDelete();
  };

  const toggleReveal = (keyId) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(keyId)) {
        next.delete(keyId);
      } else {
        next.add(keyId);
      }
      return next;
    });
  };

  const copyApiKey = async (keyId) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(keyId);
        if (setSuccess) {
          setSuccess('API key copied to clipboard');
        }
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (error) {
      console.error('Failed to copy API key', error);
      if (setError) {
        setError('Failed to copy API key to clipboard');
      }
    }
  };

  if (apiKeys.length === 0) {
    return <p className="text-slate-500 italic text-center py-4">No API keys found for this customer.</p>;
  }

  return (
    <div className="space-y-6 mb-8">
      {groupedKeys.map(({ productName, keys }) => (
        <div key={productName} className="bg-slate-900/50 border border-slate-700 rounded-lg overflow-hidden">
          <div className="bg-slate-800 px-4 py-3 border-b border-slate-700">
            <h3 className="text-sm font-semibold text-slate-200">{productName}</h3>
            <span className="text-xs text-slate-500">{keys.length} key{keys.length !== 1 ? 's' : ''}</span>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left p-3 font-medium text-xs text-slate-400 uppercase tracking-wider">Nickname</th>
                <th className="text-left p-3 font-medium text-xs text-slate-400 uppercase tracking-wider">Plan</th>
                <th className="text-left p-3 font-medium text-xs text-slate-400 uppercase tracking-wider">Usage</th>
                <th className="text-left p-3 font-medium text-xs text-slate-400 uppercase tracking-wider">Key</th>
                <th className="text-left p-3 font-medium text-xs text-slate-400 uppercase tracking-wider">Created</th>
                <th className="text-center p-3 font-medium text-xs text-slate-400 uppercase tracking-wider">Status</th>
                <th className="text-right p-3 font-medium text-xs text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(key => (
                <tr key={key.id} className="border-b border-slate-700/50 last:border-b-0 hover:bg-slate-800/30">
                  <td className="p-3 align-middle text-sm text-slate-300">
                    {editingKey === key.id ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="text"
                          className="p-1.5 bg-slate-800 rounded border border-slate-600 text-slate-100 focus:outline-none focus:border-slate-500 flex-grow min-w-[150px] text-sm"
                          value={editingNickname}
                          onChange={(e) => setEditingNickname(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveNickname(key.id);
                            else if (e.key === 'Escape') cancelEditing();
                          }}
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => saveNickname(key.id)}
                            className="px-2 py-1 text-xs bg-emerald-600 rounded hover:bg-emerald-500 transition-colors text-white"
                            disabled={!editingNickname || editingNickname === key.nickname}
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-2 py-1 text-xs bg-slate-600 rounded hover:bg-slate-500 transition-colors text-white"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium text-sm text-slate-200 truncate" title={key.nickname || 'Unnamed Key'}>
                          {key.nickname || <span className='italic text-slate-500'>Unnamed Key</span>}
                        </p>
                      </div>
                    )}
                  </td>
                  <td className="p-3 align-middle text-sm text-slate-400">{key.planName || 'N/A'}</td>
                  <td className="p-3 align-middle text-sm text-slate-400">
                    {(key.usageLimit === null || key.usageLimit === undefined) ? (
                      <span className="text-slate-500" title="No usage limit">
                        {Number(key.unitsConsumed || 0).toLocaleString()} <span className="text-slate-600">/ ∞</span>
                      </span>
                    ) : (
                      (() => {
                        const consumed = Number(key.unitsConsumed || 0);
                        const limit = Number(key.usageLimit);
                        const exhausted = consumed >= limit;
                        return (
                          <span
                            className={exhausted ? 'text-red-300 font-medium' : 'text-slate-300'}
                            title={exhausted ? 'Usage limit reached — key is inactive' : `${(limit - consumed).toLocaleString()} units remaining`}
                          >
                            {consumed.toLocaleString()} / {limit.toLocaleString()}
                          </span>
                        );
                      })()
                    )}
                  </td>
                  <td className="p-3 align-middle text-sm text-slate-400">
                    <div className="flex items-center gap-2 justify-between sm:justify-start">
                      <span className="font-mono text-xs bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-300">
                        {maskApiKey(key.id, revealedKeys)}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleReveal(key.id)}
                          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition-colors"
                          title={revealedKeys.has(key.id) ? 'Hide API key' : 'Reveal API key'}
                        >
                          {revealedKeys.has(key.id) ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          onClick={() => copyApiKey(key.id)}
                          className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition-colors"
                          title="Copy API key"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 align-middle text-sm text-slate-500">{new Date(key.created_at).toLocaleDateString()}</td>
                  <td className="p-3 align-middle text-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${key.is_enabled ? 'bg-emerald-900/50 text-emerald-300' : 'bg-red-900/50 text-red-300'}`}>
                      {key.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="p-3 align-middle text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => navigate(`/keys/${key.id}`)}
                        className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-indigo-300 transition-colors"
                        title="View key usage & limit"
                      >
                        <LineChart size={14} />
                      </button>
                      <button
                        onClick={() => handleToggleKey(key.id, key.is_enabled)}
                        className={`p-1.5 rounded transition-colors ${
                          key.is_enabled
                            ? 'text-emerald-400 hover:bg-emerald-900/30'
                            : 'text-red-400 hover:bg-red-900/30'
                        }`}
                        title={key.is_enabled ? 'Disable key' : 'Enable key'}
                      >
                        {key.is_enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                      </button>
                      <button
                        onClick={() => startEditing(key)}
                        className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
                        title="Edit nickname"
                        disabled={editingKey === key.id}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => confirmDelete(key)}
                        className="p-1.5 hover:bg-red-900/30 rounded text-red-400 hover:text-red-300 transition-colors"
                        title="Delete key"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteKeyConfirm} onOpenChange={cancelDelete}>
        <AlertDialogContent className="bg-slate-800 border border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Delete API Key</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete this API key? This action cannot be undone.
              {deleteKeyConfirm && (
                <div className="mt-2 p-2 bg-slate-900 border border-slate-700 rounded">
                  <p className="font-medium text-slate-200 truncate" title={deleteKeyConfirm.nickname || 'Unnamed Key'}>
                    {deleteKeyConfirm.nickname || 'Unnamed Key'}
                  </p>
                  <p className="text-sm font-mono text-slate-400">{maskApiKey(deleteKeyConfirm.id, revealedKeys, true)}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-slate-700 text-slate-200 hover:bg-slate-600"
              onClick={cancelDelete}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-500"
              onClick={executeDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
